import { useEffect, useRef, useState, useCallback } from "react";
import { AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { TopBar, BottomNav } from "../components/Shell";
import ProfileCard from "../components/ProfileCard";
import MatchOverlay from "../components/MatchOverlay";
import FilterModal from "../components/FilterModal";
import WalletBar from "../components/WalletBar";
import DepositModal from "../components/DepositModal";
import { X, Heart, Star, SlidersHorizontal, Loader2, Sparkles, MessageCircle } from "lucide-react";

export default function Swipe() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilter, setShowFilter] = useState(false);
  const [match, setMatch] = useState(null);
  const [depositNotice, setDepositNotice] = useState(null);
  const [starting, setStarting] = useState(false);
  const emptyLoads = useRef(0);

  const myPhoto = user?.photos?.find((p) => p.is_primary)?.url || user?.photos?.[0]?.url || user?.picture;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/discover");
      setCards(data.profiles);
      emptyLoads.current = data.profiles.length ? 0 : emptyLoads.current + 1;
    } catch {
      toast.error("Could not load profiles");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Endless deck: when the stack runs out, quietly fetch the next rotated batch
  // instead of parking on the "No more profiles" screen.
  useEffect(() => {
    if (!loading && cards.length === 0 && emptyLoads.current < 2) load();
  }, [cards, loading, load]);

  const refreshDeck = () => { emptyLoads.current = 0; load(); };

  const doSwipe = async (profile, action) => {
    setCards((prev) => prev.filter((p) => p.user_id !== profile.user_id));
    try {
      const { data } = await api.post("/swipe", { target_user_id: profile.user_id, action });
      if (data.matched) {
        setMatch({ matchId: data.match_id, user: data.user });
      } else if (action === "superlike") {
        toast(`You super liked ${profile.name}! ⭐`);
      }
    } catch {
      toast.error("Swipe failed");
    }
  };

  const top = cards[cards.length - 1];

  const startMessage = async (profile) => {
    if (starting) return;
    setStarting(true);
    try {
      const { data } = await api.post("/message/start", { target_user_id: profile.user_id });
      if (data.requires_payment && data.deposit_balance < data.price) {
        const who = profile.name && profile.name.length >= 3 ? profile.name : "this person";
        setDepositNotice(`Deposit ₹${data.price} to unlock messaging ${who}.`);
        setCards((prev) => prev.filter((p) => p.user_id !== profile.user_id));
      } else {
        setCards((prev) => prev.filter((p) => p.user_id !== profile.user_id));
        navigate(`/chat/${data.match_id}`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not open chat");
    } finally { setStarting(false); }
  };

  return (
    <>
      <TopBar
        right={
          <button data-testid="nav-filter-button" onClick={() => setShowFilter(true)}
            className="w-10 h-10 rounded-full bg-white/5 border border-white/10 grid place-items-center text-slate-300 active:scale-90 transition-transform">
            <SlidersHorizontal className="w-5 h-5" />
          </button>
        }
      />

      <WalletBar />

      <div className="flex-1 relative flex flex-col px-4 pt-3 pb-4 overflow-hidden" data-testid="swipe-card-stack">
        <div className="relative flex-1">
          {loading || (cards.length === 0 && emptyLoads.current < 2) ? (
            <div className="absolute inset-0 grid place-items-center">
              <Loader2 className="w-8 h-8 text-rose-500 animate-spin" />
            </div>
          ) : cards.length === 0 ? (
            <EmptyState onRefresh={refreshDeck} onFilter={() => setShowFilter(true)} />
          ) : (
            cards.map((profile, i) => {
              const isTop = i === cards.length - 1;
              const depth = cards.length - 1 - i;
              if (depth > 2) return null;
              return (
                <div key={profile.user_id} className="absolute inset-0"
                  style={{ transform: isTop ? "none" : `scale(${1 - depth * 0.04}) translateY(${depth * 12}px)`, zIndex: i }}>
                  {isTop ? (
                    <AnimatePresence>
                      <ProfileCard profile={profile} drag onSwipe={(action) => doSwipe(profile, action)} />
                    </AnimatePresence>
                  ) : (
                    <ProfileCard profile={profile} />
                  )}
                </div>
              );
            })
          )}
        </div>

        {!loading && cards.length > 0 && (
          <div className="flex items-center justify-center gap-4 pt-5 shrink-0">
            <ActionBtn testid="swipe-pass-button" size="md" ring="border-rose-500/40" color="text-rose-500"
              onClick={() => top && doSwipe(top, "pass")}><X className="w-7 h-7" strokeWidth={3} /></ActionBtn>
            <ActionBtn testid="swipe-superlike-button" size="sm" ring="border-cyan-400/40" color="text-cyan-400"
              onClick={() => top && doSwipe(top, "superlike")}><Star className="w-5 h-5" fill="currentColor" /></ActionBtn>
            <ActionBtn testid="swipe-like-button" size="lg" ring="border-rose-500/50" color="text-rose-500" glow
              onClick={() => top && doSwipe(top, "like")}><Heart className="w-8 h-8" fill="currentColor" /></ActionBtn>
            <ActionBtn testid="swipe-message-button" size="md" ring="border-emerald-400/50" color="text-emerald-400"
              onClick={() => top && startMessage(top)}><MessageCircle className="w-6 h-6" fill="currentColor" /></ActionBtn>
          </div>
        )}
      </div>

      <BottomNav />

      {showFilter && <FilterModal onClose={() => setShowFilter(false)} onApplied={() => { setShowFilter(false); load(); }} />}
      {depositNotice && <DepositModal notice={depositNotice} onClose={() => setDepositNotice(null)} onSuccess={() => setDepositNotice(null)} />}
      {match && (
        <MatchOverlay me={myPhoto} other={match.user.photos?.find((p) => p.is_primary)?.url || match.user.photos?.[0]?.url}
          matchId={match.matchId} otherUserId={match.user.user_id} onClose={() => setMatch(null)} />
      )}
    </>
  );
}

function ActionBtn({ children, onClick, testid, size, ring, color, glow }) {
  const dims = { sm: "w-12 h-12", md: "w-14 h-14", lg: "w-16 h-16" }[size];
  return (
    <button data-testid={testid} onClick={onClick}
      className={`${dims} ${ring} ${color} ${glow ? "animate-pulse-glow" : ""} rounded-full bg-[#1D1829] border-2 grid place-items-center active:scale-90 transition-transform shadow-lg`}>
      {children}
    </button>
  );
}

function EmptyState({ onRefresh, onFilter }) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8 gap-4" data-testid="swipe-empty-state">
      <div className="w-20 h-20 rounded-full bg-rose-500/10 grid place-items-center">
        <Sparkles className="w-9 h-9 text-rose-400" />
      </div>
      <h3 className="text-xl font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>No more profiles</h3>
      <p className="text-slate-400 text-sm max-w-[16rem]">You've seen everyone nearby for now. Widen your filters or check back soon.</p>
      <div className="flex gap-3 mt-2">
        <button onClick={onFilter} className="px-5 py-2.5 rounded-xl bg-white/10 border border-white/15 text-white text-sm font-semibold">Adjust filters</button>
        <button onClick={onRefresh} className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-rose-500 to-rose-600 text-white text-sm font-semibold">Refresh</button>
      </div>
    </div>
  );
}
